import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import { fromLocalInput, toLocalInput } from '../format';
import { emptyAutoIssue, parseTemplate, templateToJson, type AutoIssueState } from '../autoIssue';
import { AutoIssueSection } from './AutoIssueSection';
import type { AppliesTo, Campaign, CampaignInput, DiscountType } from '../types';

interface Props {
  open: boolean;
  campaign?: Campaign | null;
  /** True once redemptions exist: discount rules are locked server-side. */
  rulesLocked?: boolean;
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (input: CampaignInput) => void;
}

interface FormState {
  name: string;
  note: string;
  discountType: DiscountType;
  percent: string;
  fixed: string;
  maxDiscount: string;
  minOrder: string;
  appliesTo: AppliesTo;
  newPayersOnly: boolean;
  perUserLimit: string;
  maxRedemptions: string;
  validFrom: string;
  validTo: string;
  issue: AutoIssueState;
}

const emptyState = (): FormState => {
  const now = new Date();
  const end = new Date(now.getTime() + 30 * 86_400_000);
  return {
    name: '',
    note: '',
    discountType: 'PERCENT',
    percent: '10',
    fixed: '',
    maxDiscount: '',
    minOrder: '',
    appliesTo: 'ANY',
    newPayersOnly: false,
    perUserLimit: '1',
    maxRedemptions: '',
    validFrom: toLocalInput(now.toISOString()),
    validTo: toLocalInput(end.toISOString()),
    issue: emptyAutoIssue(),
  };
};

const fromCampaign = (c: Campaign): FormState => ({
  name: c.name,
  note: c.note ?? '',
  discountType: c.discountType,
  percent: c.percentBps ? String(c.percentBps / 100) : '',
  fixed: c.fixedAmountMinor ?? '',
  maxDiscount: c.maxDiscountMinor ?? '',
  minOrder: c.minOrderMinor ?? '',
  appliesTo: c.appliesTo,
  newPayersOnly: c.newPayersOnly,
  perUserLimit: String(c.perUserLimit),
  maxRedemptions: c.maxRedemptions ? String(c.maxRedemptions) : '',
  validFrom: toLocalInput(c.validFrom),
  validTo: toLocalInput(c.validTo),
  issue: {
    ...emptyAutoIssue(),
    trigger: c.issueTrigger,
    prefix: c.issuePrefix ?? '',
    expiresInDays: c.issueExpiresInDays ? String(c.issueExpiresInDays) : '',
    limitPerUser: String(c.issueLimitPerUser ?? 1),
    maxPerReferrer: c.issueMaxPerReferrerPerMonth ? String(c.issueMaxPerReferrerPerMonth) : '',
    ...parseTemplate(c.notificationTemplateJson),
  },
});

const digits = (value: string) => value.replace(/[^\d]/g, '');

export const CampaignFormDialog: React.FC<Props> = ({
  open,
  campaign,
  rulesLocked,
  saving,
  error,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<FormState>(emptyState);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (open) setForm(campaign ? fromCampaign(campaign) : emptyState());
  }, [open, campaign]);

  const percentBps = Math.round(Number(form.percent) * 100);
  const isFullDiscount = form.discountType === 'PERCENT' && percentBps === 10000;
  const valid =
    form.name.trim().length > 0 &&
    (form.discountType === 'PERCENT'
      ? percentBps >= 1 && percentBps <= 10000
      : Number(form.fixed) > 0) &&
    Number(form.perUserLimit) >= 1 &&
    (form.issue.trigger === 'NONE' ||
      (form.issue.prefix.length > 0 &&
        Number(form.issue.expiresInDays) >= 1 &&
        Number(form.issue.expiresInDays) <= 365 &&
        Number(form.issue.limitPerUser) >= 1));

  const submit = () => {
    const input: CampaignInput = {
      name: form.name.trim(),
      note: form.note.trim() || null,
      discountType: form.discountType,
      percentBps: form.discountType === 'PERCENT' ? percentBps : null,
      fixedAmountMinor: form.discountType === 'FIXED' ? form.fixed : null,
      maxDiscountMinor:
        form.discountType === 'PERCENT' && form.maxDiscount ? form.maxDiscount : null,
      minOrderMinor: form.minOrder || null,
      appliesTo: form.appliesTo,
      newPayersOnly: form.newPayersOnly,
      perUserLimit: Number(form.perUserLimit),
      maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
      validFrom: fromLocalInput(form.validFrom),
      validTo: fromLocalInput(form.validTo),
      issueTrigger: form.issue.trigger,
      issuePrefix: form.issue.trigger === 'NONE' ? null : form.issue.prefix,
      issueExpiresInDays: form.issue.trigger === 'NONE' ? null : Number(form.issue.expiresInDays),
      issueLimitPerUser: Number(form.issue.limitPerUser) || 1,
      issueMaxPerReferrerPerMonth:
        form.issue.trigger.startsWith('REFERRER_') && form.issue.maxPerReferrer
          ? Number(form.issue.maxPerReferrer)
          : null,
      notificationTemplateJson: form.issue.trigger === 'NONE' ? null : templateToJson(form.issue),
    };
    onSubmit(input);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{campaign ? 'Edit campaign' : 'New campaign'}</DialogTitle>
      <DialogContent>
        <div className="mt-2 space-y-4">
          {error && <Alert severity="error">{error}</Alert>}
          {rulesLocked && (
            <Alert severity="info">
              This campaign has redemptions, so the discount rules are locked. You can still change
              the name, validity and limits.
            </Alert>
          )}
          <TextField
            label="Name (internal)"
            size="small"
            fullWidth
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.note}
            onChange={(e) => set('note', e.target.value)}
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              select
              label="Discount type"
              size="small"
              disabled={rulesLocked}
              value={form.discountType}
              onChange={(e) => set('discountType', e.target.value as DiscountType)}
            >
              <MenuItem value="PERCENT">Percent</MenuItem>
              <MenuItem value="FIXED">Fixed amount (VND)</MenuItem>
            </TextField>
            {form.discountType === 'PERCENT' ? (
              <TextField
                label="Percent (%)"
                size="small"
                disabled={rulesLocked}
                value={form.percent}
                onChange={(e) => set('percent', e.target.value.replace(/[^\d.]/g, ''))}
                helperText="1 – 100"
              />
            ) : (
              <TextField
                label="Amount (VND)"
                size="small"
                disabled={rulesLocked}
                value={form.fixed}
                onChange={(e) => set('fixed', digits(e.target.value))}
              />
            )}
            {form.discountType === 'PERCENT' && (
              <TextField
                label="Max discount (VND)"
                size="small"
                value={form.maxDiscount}
                onChange={(e) => set('maxDiscount', digits(e.target.value))}
                helperText="Optional cap"
              />
            )}
            <TextField
              label="Minimum order (VND)"
              size="small"
              value={form.minOrder}
              onChange={(e) => set('minOrder', digits(e.target.value))}
              helperText="Optional"
            />
          </div>

          {isFullDiscount && (
            <Alert severity="warning">
              100% discount makes orders free. A total redemption limit is required.
            </Alert>
          )}

          <TextField
            select
            label="Applies to"
            size="small"
            fullWidth
            disabled={rulesLocked}
            value={form.appliesTo}
            onChange={(e) => set('appliesTo', e.target.value as AppliesTo)}
            helperText="Narrow down to specific items after creating (Targets)."
          >
            <MenuItem value="ANY">Packages and token packs</MenuItem>
            <MenuItem value="PACKAGE">Packages only</MenuItem>
            <MenuItem value="TOKEN_PACK">Token packs only</MenuItem>
          </TextField>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              label="Starts"
              type="datetime-local"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.validFrom}
              onChange={(e) => set('validFrom', e.target.value)}
            />
            <TextField
              label="Ends"
              type="datetime-local"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.validTo}
              onChange={(e) => set('validTo', e.target.value)}
            />
            <TextField
              label="Uses per user"
              size="small"
              value={form.perUserLimit}
              onChange={(e) => set('perUserLimit', digits(e.target.value))}
            />
            <TextField
              label="Total redemptions"
              size="small"
              value={form.maxRedemptions}
              onChange={(e) => set('maxRedemptions', digits(e.target.value))}
              helperText={isFullDiscount ? 'Required' : 'Empty = unlimited'}
              error={isFullDiscount && !form.maxRedemptions}
            />
          </div>
          <FormControlLabel
            control={
              <Switch
                checked={form.newPayersOnly}
                onChange={(e) => set('newPayersOnly', e.target.checked)}
              />
            }
            label="First-time buyers only"
          />
          <AutoIssueSection value={form.issue} onChange={(issue) => set('issue', issue)} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || saving || (isFullDiscount && !form.maxRedemptions)}
        >
          {campaign ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
